<script lang="ts">
    import {getLocalization} from 'src/core';
    import type {MediaViewerFile} from 'src/features/media-viewer/lib/mv-types';
    import {type MoveFileResult, FILE_MOVED_EVENT} from 'src/features/media-viewer/lib/mv-constants';
    import {getMediaViewerContext} from '../../../lib/mv-context';
    import {areAllFilesMailable, areAllFilesPrintable} from '../../../lib/mv-utils';
    import {onDestroy} from 'svelte';
    import {exists} from 'shared/utils/custom-utils';
    import KpContextMenuButton from 'shared/components/kp-context-menu/KpContextMenuButton.svelte';
    import MvContextMenuDivider from './MvContextMenuDivider.svelte';
    import MvContextMenuTitle from './MvContextMenuTitle.svelte';
    import {
        handleFileEditMetadataClick,
        handleFilePrintClick,
        handleFilesDeleteClick,
        handleFilesMailClick
    } from '../../../lib/mv-file-explorer-handlers';

    export let file: MediaViewerFile;

    const context = getMediaViewerContext();
    const localize = getLocalization();

    let filesSelected: MediaViewerFile[];
    let viewedFile: MediaViewerFile | null;
    const filesSelectedUnsubscribe = context.filesSelected.subscribe((currentFilesSelected) => filesSelected = currentFilesSelected);
    const viewedFileUnsubscribe = context.viewedFile.subscribe((currentViewedFile) => viewedFile = currentViewedFile);

    $: isFileSelected = exists(filesSelected.find((selectedFile) => file.id === selectedFile.id));
    $: isFileViewed = viewedFile?.id === file.id;

    onDestroy(() => {
        filesSelectedUnsubscribe();
        viewedFileUnsubscribe();
    });

    const handleSelectFileClick = () => {
        if (isFileSelected) {
            context.removeSelectedFile(file);
            return;
        }

        context.setSelectingFiles(true);

        setTimeout(() => {
            context.addSelectedFile(file);
        }, 25);
    };

    const handleViewFileClick = () => {
        context.setViewedFile(file);
    };

    const handleFileDownloadClick = async () => {
        await context.service.downloadFile(file);
    };

    const handleMoveFileClick = async () => {
        const moveResult = await context.service.moveFile(file, context.rootDirectory.id);
        if (!exists(moveResult)) {
            return;
        }

        context.eventBus.dispatchEvent(new CustomEvent<MoveFileResult>(FILE_MOVED_EVENT, {detail: moveResult}));
    };
</script>

<MvContextMenuTitle>
    {localize(/* @kp-localization mediaviewer.fileExplorer.FileContextLabel */ 'mediaviewer.fileExplorer.FileContextLabel')}
</MvContextMenuTitle>

<MvContextMenuDivider/>

{#if isFileViewed}
    <KpContextMenuButton icon="eye"
                         on:click={handleViewFileClick}>

        {localize(/* @kp-localization mediaviewer.fileExplorer.ShowFile */ 'mediaviewer.fileExplorer.ShowFile')}
    </KpContextMenuButton>
{/if}

{#if context.canEdit}
    {#if isFileViewed}
        <MvContextMenuDivider/>
    {/if}

    <KpContextMenuButton icon="edit"
                         on:click={() => handleFileEditMetadataClick(context, file)}>

        {localize(/* @kp-localization mediaviewer.fileExplorer.EditFileMetadata */ 'mediaviewer.fileExplorer.EditFileMetadata')}
    </KpContextMenuButton>

    <KpContextMenuButton icon="move-to-folder-2"
                         on:click={handleMoveFileClick}>

        {localize(/* @kp-localization mediaviewer.fileExplorer.MoveFile */ 'mediaviewer.fileExplorer.MoveFile')}
    </KpContextMenuButton>

    <MvContextMenuDivider/>

    <KpContextMenuButton icon={isFileSelected ? 'cross-circle' : 'checkbox'}
                         buttonStyle={isFileSelected ? 'brand-orange' : 'default'}
                         on:click={handleSelectFileClick}>

        {isFileSelected
            ? localize(/* @kp-localization mediaviewer.fileExplorer.CancelFileSelection */ 'mediaviewer.fileExplorer.CancelFileSelection')
            : localize(/* @kp-localization mediaviewer.fileExplorer.SelectFile */ 'mediaviewer.fileExplorer.SelectFile')}
    </KpContextMenuButton>
{/if}

{#if file.exportPermission.allowed}
    <KpContextMenuButton icon="download"
                         on:click={handleFileDownloadClick}>

        {localize(/* @kp-localization mediaviewer.fileExplorer.DownloadFile */ 'mediaviewer.content.ActionsDownload')}
    </KpContextMenuButton>
{/if}

{#if areAllFilesPrintable([file])}
    <KpContextMenuButton icon="print"
                         on:click={() => handleFilePrintClick(context, file)}>

        {localize(/* @kp-localization mediaviewer.fileExplorer.PrintFile */ 'mediaviewer.fileExplorer.PrintFile')}
    </KpContextMenuButton>
{/if}

{#if areAllFilesMailable([file])}
    <KpContextMenuButton icon="envelope"
                         on:click={() => handleFilesMailClick(context, [file])}>

        {localize(/* @kp-localization mediaviewer.fileExplorer.EmailFile */ 'mediaviewer.fileExplorer.EmailFile')}
    </KpContextMenuButton>
{/if}

{#if context.canEdit}
    <MvContextMenuDivider/>

    <KpContextMenuButton icon="trash"
                         buttonStyle="danger"
                         on:click={() => handleFilesDeleteClick(context, [file])}>

        {localize(/* @kp-localization mediaviewer.fileExplorer.DeleteFile */ 'mediaviewer.fileExplorer.DeleteFile')}
    </KpContextMenuButton>
{/if}